import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Settings, Briefcase, Mountain, Bike } from 'lucide-react';
import './AdvancedFilters.css';

export const AdvancedFilters = ({ filters, onFilterChange, onApply, onReset }) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  return (
    <div className="advanced-filters">
      <button 
        className="filters-toggle"
        onClick={() => setExpanded(!expanded)}
        data-testid="advanced-filters-toggle"
      >
        <Settings size={20} />
        <span>{t('advancedFilters.title')}</span>
        <span className="toggle-icon">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="filters-panel" data-testid="advanced-filters-panel">
          {/* LUGGAGE */}
          <div className="filter-group">
            <h3>{t('advancedFilters.luggage')}</h3>
            <div className="number-selector">
              {[1, 2, 3, '4+'].map((num) => (
                <button
                  key={num}
                  className={`number-btn ${filters.luggage === num ? 'active' : ''}`}
                  onClick={() => onFilterChange('luggage', num)}
                >
                  {num}
                </button>
              ))}
            </div>
            <div className="chip-selector">
              <button 
                className={`chip ${filters.backpack ? 'active' : ''}`}
                onClick={() => onFilterChange('backpack', !filters.backpack)}
              >
                <Briefcase size={16} />
                <span>{t('advancedFilters.backpack')}</span>
              </button>
              <button 
                className={`chip ${filters.ski ? 'active' : ''}`}
                onClick={() => onFilterChange('ski', !filters.ski)}
              >
                <Mountain size={16} />
                <span>{t('advancedFilters.ski')}</span>
              </button>
              <button 
                className={`chip ${filters.bicycle ? 'active' : ''}`}
                onClick={() => onFilterChange('bicycle', !filters.bicycle)}
              >
                <Bike size={16} />
                <span>{t('advancedFilters.bicycle')}</span>
              </button>
            </div>
          </div>

          {/* PREFERENCES */}
          <div className="filter-group preferences-grid">
            <h3>{t('advancedFilters.preferences')}</h3>
            
            {/* Smoking */}
            <div className="preference-group">
              <h4>{t('advancedFilters.smoking')}</h4>
              <label>
                <input 
                  type="radio" 
                  name="smoking" 
                  value="any"
                  checked={filters.smoking === 'any'}
                  onChange={(e) => onFilterChange('smoking', e.target.value)}
                />
                <span>{t('advancedFilters.any')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="smoking" 
                  value="allowed"
                  checked={filters.smoking === 'allowed'}
                  onChange={(e) => onFilterChange('smoking', e.target.value)}
                />
                <span>{t('advancedFilters.allowed')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="smoking" 
                  value="not_allowed"
                  checked={filters.smoking === 'not_allowed'}
                  onChange={(e) => onFilterChange('smoking', e.target.value)}
                />
                <span>{t('advancedFilters.notAllowed')}</span>
              </label>
            </div>

            {/* Pets */}
            <div className="preference-group">
              <h4>{t('advancedFilters.pets')}</h4>
              <label>
                <input 
                  type="radio" 
                  name="pets" 
                  value="any"
                  checked={filters.pets === 'any'}
                  onChange={(e) => onFilterChange('pets', e.target.value)}
                />
                <span>{t('advancedFilters.any')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="pets" 
                  value="allowed"
                  checked={filters.pets === 'allowed'}
                  onChange={(e) => onFilterChange('pets', e.target.value)}
                />
                <span>{t('advancedFilters.allowed')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="pets" 
                  value="not_allowed"
                  checked={filters.pets === 'not_allowed'}
                  onChange={(e) => onFilterChange('pets', e.target.value)}
                />
                <span>{t('advancedFilters.notAllowed')}</span>
              </label>
            </div>

            {/* Music */}
            <div className="preference-group">
              <h4>{t('advancedFilters.music')}</h4>
              <label>
                <input 
                  type="radio" 
                  name="music" 
                  value="any"
                  checked={filters.music === 'any'}
                  onChange={(e) => onFilterChange('music', e.target.value)}
                />
                <span>{t('advancedFilters.any')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="music" 
                  value="none"
                  checked={filters.music === 'none'}
                  onChange={(e) => onFilterChange('music', e.target.value)}
                />
                <span>{t('advancedFilters.none')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="music" 
                  value="quiet"
                  checked={filters.music === 'quiet'}
                  onChange={(e) => onFilterChange('music', e.target.value)}
                />
                <span>{t('advancedFilters.quiet')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="music" 
                  value="normal"
                  checked={filters.music === 'normal'}
                  onChange={(e) => onFilterChange('music', e.target.value)}
                />
                <span>{t('advancedFilters.normal')}</span>
              </label>
            </div>

            {/* Conversation */}
            <div className="preference-group">
              <h4>{t('advancedFilters.conversation')}</h4>
              <label>
                <input 
                  type="radio" 
                  name="conversation" 
                  value="any"
                  checked={filters.conversation === 'any'}
                  onChange={(e) => onFilterChange('conversation', e.target.value)}
                />
                <span>{t('advancedFilters.any')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="conversation" 
                  value="quiet"
                  checked={filters.conversation === 'quiet'}
                  onChange={(e) => onFilterChange('conversation', e.target.value)}
                />
                <span>{t('advancedFilters.quiet')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="conversation" 
                  value="normal"
                  checked={filters.conversation === 'normal'}
                  onChange={(e) => onFilterChange('conversation', e.target.value)}
                />
                <span>{t('advancedFilters.normal')}</span>
              </label>
              <label>
                <input 
                  type="radio" 
                  name="conversation" 
                  value="chatty"
                  checked={filters.conversation === 'chatty'}
                  onChange={(e) => onFilterChange('conversation', e.target.value)}
                />
                <span>{t('advancedFilters.chatty')}</span>
              </label>
            </div>
          </div>

          {/* VEHICLE */}
          <div className="filter-group">
            <h3>{t('advancedFilters.vehicle')}</h3>
            <div className="vehicle-type-selector">
              <label>{t('advancedFilters.vehicleType')}</label>
              <select 
                value={filters.vehicleType || ''}
                onChange={(e) => onFilterChange('vehicleType', e.target.value)}
              >
                <option value="">{t('advancedFilters.selectType')}</option>
                <option value="sedan">{t('advancedFilters.sedan')}</option>
                <option value="suv">{t('advancedFilters.suv')}</option>
                <option value="van">{t('advancedFilters.van')}</option>
                <option value="coupe">{t('advancedFilters.coupe')}</option>
                <option value="hatchback">{t('advancedFilters.hatchback')}</option>
              </select>
            </div>
            <div className="vehicle-features">
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={filters.ac || false}
                  onChange={(e) => onFilterChange('ac', e.target.checked)}
                />
                <span>{t('advancedFilters.ac')}</span>
              </label>
              <label className="checkbox-label">
                <input 
                  type="checkbox"
                  checked={filters.spacious || false}
                  onChange={(e) => onFilterChange('spacious', e.target.checked)}
                />
                <span>{t('advancedFilters.spacious')}</span>
              </label>
            </div>
          </div>

          {/* DRIVER */}
          <div className="filter-group">
            <h3>{t('advancedFilters.driver')}</h3>
            <label className="checkbox-label">
              <input 
                type="checkbox"
                checked={filters.verifiedOnly || false}
                onChange={(e) => onFilterChange('verifiedOnly', e.target.checked)}
              />
              <span>{t('advancedFilters.verifiedOnly')}</span>
            </label>
            <div className="rating-slider">
              <label>{t('advancedFilters.rating')} ({t('advancedFilters.minStars', { rating: (filters.minRating || 4.0).toFixed(1) })})</label>
              <input 
                type="range"
                min="1"
                max="5"
                step="0.1"
                value={filters.minRating || 4.0}
                onChange={(e) => onFilterChange('minRating', parseFloat(e.target.value))}
              />
              <div className="rating-display">
                {(filters.minRating || 4.0).toFixed(1)}+ {t('advancedFilters.stars')}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="filter-actions">
            <button className="btn-reset" onClick={onReset} data-testid="filters-reset-button">
              {t('advancedFilters.reset')}
            </button>
            <button className="btn-apply" onClick={onApply} data-testid="filters-apply-button">
              {t('advancedFilters.apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
